import axios from "axios";
import { IBranding } from "lobbie";
import { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import SchedulingBrandingLogo from "src/components/shared/scheduling/cards/SchedulingBrandingLogo";
import { BASE_API_URL, BASE_PUBLIC_API_ROUTE } from "src/constants";

interface IInvoiceLineItemDisplay {
    name: string;
    amountInCents: number;
}

interface IPaymentData {
    firstName: string;
    lastName: string;
    homeAddress: string;
    homeAddress2?: string;
    invoiceLineItems: IInvoiceLineItemDisplay[];
    totalAmountInCents: number;
    surcharge: boolean;
    branding?: IBranding;
}

const PatientRemoteCareConversion = () => {
    const [remoteConversionUUID, setRemoteConversionUUID] = useState<string | null>(null);
    const [paymentData, setPaymentData] = useState<IPaymentData | undefined>();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const ctuValue = queryParams.get("ctu");
        if (ctuValue) {
            setRemoteConversionUUID(ctuValue);
        }
    }, [location.search]);

    useEffect(() => {
        if (remoteConversionUUID) {
            axios
                .get(
                    `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care/patient-payment-info/${remoteConversionUUID}`,
                )
                .then((response) => {
                    if (response?.data) {
                        setPaymentData(response.data as IPaymentData);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching remote conversion data:", error);
                });
        }
    }, [remoteConversionUUID]);

    const surchargeAmount = useMemo(() => {
        if (paymentData) {
            return paymentData.totalAmountInCents * 0.035;
        }
        return 0;
    }, [paymentData]);
    if (!paymentData) {
        return <FullScreenLoadingSpinner />;
    }
    return (
        <div className="container">
            <div className="row mt-4">
                {paymentData?.branding && (
                    <>
                        <div className="col-3">
                            <SchedulingBrandingLogo branding={paymentData?.branding} />
                        </div>
                        <div className="col-9">&nbsp;</div>
                    </>
                )}
            </div>
            <div className="row mt-4">
                <div className="col-6">
                    <h4>Payment Information</h4>
                    <hr />
                    <Form>
                        <div className="row">
                            <div className="col-12">
                                <Form.Group className="mb-3">
                                    <Form.Label>Cardholder Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={paymentData.firstName + " " + paymentData.lastName}
                                        readOnly
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <Form.Group className="mb-3">
                                    <Form.Label>Card Number</Form.Label>
                                    <Form.Control type="text" readOnly />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Expiration Date</Form.Label>
                                    <Form.Control type="text" readOnly />
                                </Form.Group>
                            </div>
                            <div className="col-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>CVV</Form.Label>
                                    <Form.Control type="text" readOnly />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-9">
                                <Form.Group className="mb-3">
                                    <Form.Label>Billing Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={paymentData.homeAddress}
                                        readOnly
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>Apt/Ste #</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={paymentData.homeAddress2}
                                        readOnly
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col text-right">
                                <Button type="submit" variant="primary">
                                    Process Payment
                                </Button>
                            </div>
                        </div>
                    </Form>
                </div>
                <div className="col-6">
                    <h4>Invoice Details</h4>
                    <hr />
                    <table className="table table-bordered" style={{ maxWidth: "600px" }}>
                        <tbody>
                            {paymentData.invoiceLineItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>${(item.amountInCents / 100).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className="bold">
                                    {paymentData.surcharge ? "Subtotal" : "Total"}
                                </td>
                                <td className="bold">
                                    ${(paymentData.totalAmountInCents / 100).toFixed(2)}
                                </td>
                            </tr>
                            {paymentData.surcharge && (
                                <tr>
                                    <td className="bold">Total (3.5% Surcharge)</td>
                                    <td className="bold">
                                        $
                                        {(
                                            (paymentData.totalAmountInCents + surchargeAmount) /
                                            100
                                        ).toFixed(2)}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};
export default PatientRemoteCareConversion;
